'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function MezoterapininIgneliBirIslemOlmasi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/mezoterapinin-igneli-bir-islem-olmasi-gercekler-faydalar-ve-guvenlik';
  const pageTitle = 'Mezoterapinin İğneli Bir İşlem Olması: Gerçekler, Faydalar ve Güvenlik';
  const pageDescription = 'Mezoterapinin iğneli bir işlem olmasının avantajları nelerdir? Ağrı, kızarıklık, iyileşme süreci ve steril uygulama detayları.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Mezoterapinin İğneli Bir İşlem Olması', href: '/tr/blog/mezoterapinin-igneli-bir-islem-olmasi-gercekler-faydalar-ve-guvenlik' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Mezoterapi iğneli uygulama süreci"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Mezoterapi & Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Mezoterapinin İğneli Bir İşlem Olması: Gerçekler, Faydalar ve Güvenlik
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>22 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Mezoterapi, adından da anlaşılacağı gibi "cildin orta katmanına" (mezoderm) mikro enjeksiyonlarla aktif maddelerin verilmesidir.
                Yani iğneli bir işlemdir — fakat bu tanım, düşündüğünüz kadar korkutucu değildir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Günümüzde kullanılan mikro iğne sistemleri ve topikal anesteziklerle işlem neredeyse ağrısız, etkisi ise derin ve kalıcıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Mezoterapi Neden İğneli Bir Uygulamadır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Cilt yüzeyine sürülen serumlar yalnızca epidermis tabakasında kalır.
                Oysa yaşlanma, nem kaybı, elastikiyet azalması gibi sorunlar daha derin tabakalarda, yani mezoderm seviyesinde oluşur.
              </p>

              <div className="bg-amber-50 rounded-xl p-6 mb-6">
                <p className="text-amber-800 mb-4 font-medium">
                  İğne kullanımı sayesinde:
                </p>
                <ul className="list-disc list-inside space-y-2 text-amber-800">
                  <li>✅ Aktif maddeler doğrudan hedef tabakaya ulaşır,</li>
                  <li>✅ Emilim oranı %95'e çıkar,</li>
                  <li>✅ Hücre yenilenmesi uyarılır,</li>
                  <li>✅ Dolaşım artar, kolajen sentezi başlar.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Kullanılan Ekipmanlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-amber-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Ekipman</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Özellik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Kullanım Alanı</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Mikro iğne tabancası (mesogun)</td>
                      <td className="border border-gray-300 px-4 py-3">0.4–1.5 mm iğnelerle hassas dozlama</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz, boyun, dekolte</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Manuel mikro iğne</td>
                      <td className="border border-gray-300 px-4 py-3">Klasik yöntem, hekim kontrolünde</td>
                      <td className="border border-gray-300 px-4 py-3">Lokal uygulamalar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Derma roller / pen</td>
                      <td className="border border-gray-300 px-4 py-3">Yüzeysel vitamin protokolleri için</td>
                      <td className="border border-gray-300 px-4 py-3">Genç ciltlerde</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-800 font-medium">
                  Tüm cihazlar tek kullanımlık steril uçlarla çalışır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Acı ve Konfor Düzeyi
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Modern mezoterapi uygulamalarında:
                </p>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>İşlemden 20 dakika önce lokal anestezik krem uygulanır.</li>
                  <li>Mikro iğneler saniyede onlarca enjeksiyon yapar, bu da acı hissini minimize eder.</li>
                  <li>İşlem sonrası hafif batma veya yanma 1–2 saat içinde geçer.</li>
                </ul>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-900 font-medium">
                    💬 Yani, his olarak bir "kaş alma" acısından daha fazla değildir.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Mezoterapi Uygulamasının Adımları
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside space-y-3 text-purple-800">
                  <li>Cilt antiseptik solüsyonla temizlenir.</li>
                  <li>Anestezik krem uygulanır.</li>
                  <li>Hekim, kişiye özel mezokokteyli belirler.</li>
                  <li>Mikro enjeksiyonlarla 1–2 cm aralıklarla madde uygulanır.</li>
                  <li>Sonrasında yatıştırıcı maske ve LED terapiyle kapanış yapılır.</li>
                </ol>
                <p className="text-purple-800 mt-4 font-medium">
                  Toplam süre: yaklaşık 40 dakika.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. İğneli Mezoterapinin Avantajları
              </h2>
              
              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-indigo-600 mr-2">✅</span>
                      <span className="text-indigo-800">Derin tabakalarda aktif madde etkisi</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-indigo-600 mr-2">✅</span>
                      <span className="text-indigo-800">Kolajen ve elastin üretimi artışı</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-indigo-600 mr-2">✅</span>
                      <span className="text-indigo-800">Doku sıkılaşması</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-indigo-600 mr-2">✅</span>
                      <span className="text-indigo-800">Cilt tonunun dengelenmesi</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-indigo-600 mr-2">✅</span>
                      <span className="text-indigo-800">Uzun kalıcılık (4–6 ay)</span>
                    </div>
                  </div>
                </div>
                <div className="bg-indigo-100 rounded-lg p-4 mt-4">
                  <p className="text-indigo-900 font-medium">
                    Bu etki, iğnesiz yöntemlerle (ör. mezopatch veya iyontoforez) sağlanamaz.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Olası Yan Etkiler ve Önlemler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yan Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif kızarıklık</td>
                      <td className="border border-gray-300 px-4 py-3">1–2 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Normal cilt tepkisidir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ödem / şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">1–2 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Soğuk kompresle azalır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Minik morluk</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Hassas ciltlerde görülebilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  Uygulama öncesi aspirin, alkol veya kan sulandırıcı ilaçlar kullanılmamalıdır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Sterilizasyon ve Güvenlik
              </h2>
              
              <div className="bg-teal-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-teal-800">
                  <li>Tüm iğneler tek kullanımlıktır</li>
                  <li>Cilt antiseptikle hazırlanır</li>
                  <li>Klinik ortamda hekim tarafından uygulanır</li>
                  <li>Kullanılan serumlar CE ve Sağlık Bakanlığı onaylı olmalıdır</li>
                </ul>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                  <p className="text-red-800 font-medium">
                    ⚠️ Evde veya güzellik merkezinde yapılan iğneli uygulamalar enfeksiyon riski taşır ve tıbben önerilmez.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "İğne kelimesi hastalarda kaygı yaratabilir, fakat modern mezoterapi sistemleri son derece konforludur.
                  Cildin alt tabakasına ulaşabilmek için iğne gereklidir; çünkü etkiyi veren, tam da o derinliğe ulaşabilmektir."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Mezoterapi ağrılı mı?</h3>
                  <p className="text-gray-600">Hayır. Anestezik kremle birlikte rahatsızlık hissi minimumdur.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Kızarıklık ne kadar sürer?</h3>
                  <p className="text-gray-600">Genellikle 2–3 saat içinde tamamen geçer.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. İğnesiz mezoterapi aynı etkiyi verir mi?</h3>
                  <p className="text-gray-600">Hayır, cilt altına madde ulaşmadığı için etki yüzeyseldir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Enfeksiyon riski var mı?</h3>
                  <p className="text-gray-600">Steril klinik koşullarda yapılırsa risk yoktur.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS Global Report (2024) – "Mesotherapy Injection Techniques and Pain Control"</li>
                  <li>Harvard Health Publishing (2024) – "The Science of Mesotherapy Delivery Systems"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Mezoterapide Sterilizasyon ve Güvenlik Rehberi"</li>
                  <li>Mayo Clinic (2023) – "Skin Needling and Collagen Activation Studies"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#mezoterapi</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#iğneli</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#ciltbakımı</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#kolajen</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-amber-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Mezoterapi Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan mezoterapi tedavisi hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Mezoterapi hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/cilt-kurulugunda-mezoterapi-derin-nem-destegi-ve-hucresel-yenilenme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Cilt Kuruluğunda Mezoterapi</h4>
              <p className="text-gray-600 text-sm">Cilt kuruluğuna karşı mezoterapi nasıl etki eder? Derin nem desteği ve hücresel yenilenme.</p>
            </a>
            <a
              href="/tr/blog/kis-aylarinda-uygulanan-mezoterapiler-soguk-havada-cilt-yenilenmesi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Kış Aylarında Mezoterapiler</h4>
              <p className="text-gray-600 text-sm">Kış mevsiminde mezoterapi neden daha etkili olur? Soğuk havada cilt yenilenmesi.</p>
            </a>
            <a
              href="/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Mezoterapinin Etkileri Ne Zaman Görülür?</h4>
              <p className="text-gray-600 text-sm">Mezoterapi uygulamasından sonra etkilerin ne zaman görülmeye başladığı hakkında bilgiler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
